/**
 * RemotionScenePreview Component
 *
 * Remotion-based replacement for ScenePreviewCanvas.
 * Uses RemotionOverlayRenderer for preview and EditableOverlayLayer for interaction.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStorage, ref } from "firebase/storage";
import { Loader2 } from "lucide-react";
import { AnyOverlay, ImageFilter } from "../../models/Post";
import { RemotionOverlayRenderer } from "../../remotion/RemotionOverlayRenderer";
import { EditableOverlayLayer } from "./EditableOverlayLayer";
import { getSignedUrl } from "../../services/Firebase";

interface RemotionScenePreviewProps {
  backgroundColor?: string;
  backgroundImagePath?: string;
  backgroundImageUrl?: string; // Direct URL for preview (takes precedence)
  backgroundFilter?: ImageFilter;
  overlays: AnyOverlay[];
  onOverlayChange?: (updatedOverlay: AnyOverlay) => void;
  onSelectOverlay?: (overlayId: string | null) => void;
  selectedOverlayId?: string | null;
  overlayPreviewUrls?: Record<string, string>;
  referenceMediaWidth?: number;
  /** Called once when a drag/resize/rotate interaction begins on the canvas */
  onInteractionStart?: () => void;
  /** Called when backgroundImageUrl is resolved from backgroundImagePath fallback */
  onBackgroundImageResolved?: (url: string) => void;
  onDeleteOverlay?: (id: string) => void;
  onDuplicateOverlay?: (id: string) => void;
  onCopyOverlay?: (overlay: AnyOverlay) => void;
  onPasteOverlay?: () => void;
}

export const RemotionScenePreview: React.FC<RemotionScenePreviewProps> = ({
  backgroundColor = "#E5E7EB",
  backgroundImagePath,
  backgroundImageUrl,
  backgroundFilter,
  overlays = [],
  onOverlayChange,
  onSelectOverlay,
  selectedOverlayId,
  overlayPreviewUrls,
  referenceMediaWidth,
  onInteractionStart,
  onBackgroundImageResolved,
  onDeleteOverlay,
  onDuplicateOverlay,
  onCopyOverlay,
  onPasteOverlay,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [loadedBgUrl, setLoadedBgUrl] = useState<string | null>(
    // Initialize with direct URL if provided
    backgroundImageUrl || null
  );
  const [imageUrls, setImageUrls] = useState<Record<string, string>>(
    // Initialize with any provided preview URLs
    overlayPreviewUrls || {}
  );

  // Track if initial loading is complete
  const [bgUrlResolved, setBgUrlResolved] = useState(
    // Already resolved if we have a direct URL or no path to load
    !!backgroundImageUrl || !backgroundImagePath
  );
  const [overlayUrlsResolved, setOverlayUrlsResolved] = useState(false);
  const [hasRenderedOnce, setHasRenderedOnce] = useState(false);

  // Keep a stable ref for the callback to avoid re-triggering the effect
  const onBgResolvedRef = useRef(onBackgroundImageResolved);
  onBgResolvedRef.current = onBackgroundImageResolved;

  // Load background image URL from storage path
  useEffect(() => {
    if (backgroundImageUrl) {
      setLoadedBgUrl(backgroundImageUrl);
      setBgUrlResolved(true);
      return;
    }

    if (!backgroundImagePath) {
      setLoadedBgUrl(null);
      setBgUrlResolved(true);
      return;
    }

    let isMounted = true;
    setBgUrlResolved(false);
    const storage = getStorage();
    const storageRef = ref(storage, backgroundImagePath);

    getSignedUrl(storageRef.fullPath)
      .then((url) => {
        if (isMounted) {
          setLoadedBgUrl(url);
          setBgUrlResolved(true);
          onBgResolvedRef.current?.(url);
        }
      })
      .catch((error) => {
        console.error("Error loading background image:", error);
        if (isMounted) {
          setLoadedBgUrl(null);
          setBgUrlResolved(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [backgroundImagePath, backgroundImageUrl]);

  // Load image overlay URLs
  useEffect(() => {
    let isMounted = true;

    const loadImageUrls = async () => {
      const storage = getStorage();
      const newUrls: Record<string, string> = { ...(overlayPreviewUrls || {}) };

      // Find image overlays that need URL loading
      const imageOverlaysToLoad = overlays.filter(
        (overlay) =>
          overlay.type === "image" &&
          "filePath" in overlay &&
          overlay.filePath &&
          !newUrls[overlay.filePath]
      );

      // Load each URL
      for (const overlay of imageOverlaysToLoad) {
        if ("filePath" in overlay && overlay.filePath) {
          try {
            const storageRef = ref(storage, overlay.filePath);
            const url = await getSignedUrl(storageRef.fullPath);
            newUrls[overlay.filePath] = url;
          } catch (error) {
            console.error("Error loading overlay image:", error, overlay.filePath);
          }
        }
      }

      if (isMounted) {
        setImageUrls((prev) => {
          const prevKeys = Object.keys(prev);
          const nextKeys = Object.keys(newUrls);
          if (prevKeys.length !== nextKeys.length) return newUrls;
          for (const key of nextKeys) {
            if (prev[key] !== newUrls[key]) return newUrls;
          }
          return prev;
        });
        setOverlayUrlsResolved(true);
      }
    };

    loadImageUrls();

    return () => {
      isMounted = false;
    };
  }, [overlays, overlayPreviewUrls]);

  // Determine if we're still loading (waiting for initial URL resolution)
  const isLoading = !bgUrlResolved || !overlayUrlsResolved;
  const canRenderCanvas =
    canvasSize.width > 0 &&
    canvasSize.height > 0 &&
    (!isLoading || hasRenderedOnce);

  useEffect(() => {
    if (canvasSize.width > 0 && canvasSize.height > 0 && !isLoading) {
      setHasRenderedOnce(true);
    }
  }, [canvasSize.width, canvasSize.height, isLoading]);

  // Measure container size
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Merge overlay preview URLs with loaded URLs
  const mergedImageUrls = useMemo(() => {
    return { ...imageUrls, ...(overlayPreviewUrls || {}) };
  }, [imageUrls, overlayPreviewUrls]);

  // Handle overlay updates (memoized to prevent listener re-registration in EditableOverlayLayer)
  const handleOverlayUpdate = useCallback(
    (updatedOverlay: AnyOverlay) => {
      onOverlayChange?.(updatedOverlay);
    },
    [onOverlayChange]
  );

  // Handle selection
  const handleSelect = useCallback(
    (id: string | null) => {
      onSelectOverlay?.(id);
    },
    [onSelectOverlay]
  );

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        backgroundColor,
      }}
    >
      {/* Loading spinner */}
      {isLoading && !hasRenderedOnce && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      )}

      {/* Remotion Overlay Renderer - clipped to frame */}
      {canRenderCanvas && (
        <>
          <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
            <RemotionOverlayRenderer
              overlays={overlays}
              containerWidth={canvasSize.width}
              containerHeight={canvasSize.height}
              backgroundColor={backgroundColor}
              backgroundImageUrl={loadedBgUrl || undefined}
              backgroundFilter={backgroundFilter}
              imageUrls={mergedImageUrls}
              referenceWidth={referenceMediaWidth}
              layoutEngine="remotion"
            />
          </div>

          {/* Editing Layer — clipped to canvas so off-boundary overlays don't intercept outside clicks */}
          <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
          <EditableOverlayLayer
            overlays={overlays}
            selectedOverlayId={selectedOverlayId || null}
            containerWidth={canvasSize.width}
            containerHeight={canvasSize.height}
            onSelect={handleSelect}
            onUpdate={handleOverlayUpdate}
            onDelete={onDeleteOverlay}
            onDuplicate={onDuplicateOverlay}
            onCopy={onCopyOverlay}
            onPaste={onPasteOverlay}
            onInteractionStart={onInteractionStart}
            referenceWidth={referenceMediaWidth}
            imageUrls={mergedImageUrls}
          />
          </div>
        </>
      )}
    </div>
  );
};

export default RemotionScenePreview;
